/* eslint-disable react/prop-types */
import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'

import filter from 'lodash/filter'
import find from 'lodash/find'
import isEmpty from 'lodash/isEmpty'
import round from 'lodash/round'
import toNumber from 'lodash/toNumber'

import { useT } from '@smartcoop/i18n'
import { arrowLeft } from '@smartcoop/icons'
import { getLandRegistries } from '@smartcoop/services/apis/smartcoopApi/resources/field'
import { FieldActions } from '@smartcoop/stores/field'
import { colors } from '@smartcoop/styles'
import Badge from '@smartcoop/web-components/Badge'
import Button from '@smartcoop/web-components/Button'
import CheckboxButton from '@smartcoop/web-components/CheckboxGroup/CheckboxButton'
import DataTable from '@smartcoop/web-components/DataTable'
import Icon from '@smartcoop/web-components/Icon'
import Modal from '@smartcoop/web-components/Modal'

const FieldLandRegistryModal = (props) => {
  const { id, open, field, selectedLandRegistries, setSelectedLandRegistries, handleClose } = props
  const t = useT()

  const tableRef = useRef(null)

  const [landRegistries, setLandRegistries] = useState([])
  const [loading, setLoading] = useState(false)
  const dispatch = useCallback(useDispatch(), [])

  const getFieldLandRegistries = useCallback(async () => {
    setLoading(true)
    const { data } = await getLandRegistries({}, { fieldId: field?.id })

    setLandRegistries(data)
    setLoading(false)
  }, [field])

  useEffect(() => {
    getFieldLandRegistries()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const seeOnMap = useCallback((data) => {
    setSelectedLandRegistries(old => {
      const foundRegistry = filter(old, item => item?.landRegistryId === data?.landRegistryId)
      if(!isEmpty(foundRegistry)) {
        return filter(old, item => item?.landRegistryId !== data?.landRegistryId)
      }
      return [...old, data]
    })
    handleClose()
  }, [handleClose, setSelectedLandRegistries])

  const seeAllOnMap = useCallback(() => {
    setSelectedLandRegistries(old => old.length === landRegistries.length ? [] : landRegistries)
    handleClose()
  }, [handleClose, landRegistries, setSelectedLandRegistries])

  const columns = useMemo(
    () => [
      {
        title: '',
        field: 'fieldLandRegistryId',
        render: row => (
          <CheckboxButton
            onChange={ () => {
              setLoading(true)
              if(row?.fieldLandRegistryId) {
                dispatch(FieldActions.unbindLandRegistry(row?.fieldLandRegistryId, getFieldLandRegistries, getFieldLandRegistries))
              } else {
                dispatch(FieldActions.bindLandRegistry({ fieldLandRegistryId: row?.fieldLandRegistryId, fieldId: field?.id, landRegistryId: row?.landRegistryId }, getFieldLandRegistries, getFieldLandRegistries))
              }
            } }
            checked={ !!row?.fieldLandRegistryId }
            value={ !!row?.fieldLandRegistryId }
            disabled={ loading }
            style={ { width: 50 } }
          />
        ),
        width: '5%'
      },
      {
        title: t('property code'),
        field: 'landRegistry.propertyCode',
        sorting: true
      },
      {
        title: t('situation'),
        field: 'landRegistry.conditionDescription',
        sorting: true
      },
      {
        title: t('area'),
        field: 'landRegistry.area',
        sorting: true,
        render: (row) => `${ round(toNumber(row?.landRegistry?.area), 2) } ha`,
        align: 'right',
        width: '5%'
      },
      {
        title: t('intersection'),
        field: 'intersectionPercentage',
        sorting: true,
        render: (row) => `${ round(toNumber(row?.intersectionPercentage), 2) } %`,
        align: 'right',
        width: '5%'
      },
      {
        title: t('status'),
        field: 'status',// pending | confirmed
        sorting: true,
        render: (row) => (
          <Badge
            backgroundColorBadge={ row?.status === 'confirmed' ? colors.green : colors.warning  }
            colorBadge={ row?.status === 'confirmed' ?  colors.green : colors.warning }
          >
            {row?.status === 'confirmed' ? t('confirmed') : t('pending') }
          </Badge>
        ),
        width: '5%'
      },
      {
        title: <Button
          id="see-all-geometry-on-map"
          color={ selectedLandRegistries.length === landRegistries.length ? colors.black : colors.white }
          onClick={ seeAllOnMap }
          disabled={ isEmpty(landRegistries) }
          style={ { width: '10vw' } }
        >
          <>
            {t('view short all')}
            <Icon size={ 14 } style={ { transform: 'rotate(180deg)', marginLeft: 5 } } icon={ arrowLeft } color={ selectedLandRegistries.length === landRegistries.length ? colors.white : colors.black  } />
          </>
        </Button>
        ,
        field: 'seeOnMap',
        render: row => (
          <Button
            id={ `see-geometry-on-map-${ row?.landRegistryId }` }
            color={ find(selectedLandRegistries, item => item?.landRegistryId === row?.landRegistryId) ? colors.black : colors.white }
            onClick={ () => seeOnMap(row) }
            disabled={ isEmpty(row?.intersectionGeom) }
            style={ { width: '10vw' } }
          >
            <>
              {t('view')}
              <Icon size={ 14 } style={ { transform: 'rotate(180deg)', marginLeft: 5 } } icon={ arrowLeft } color={ find(selectedLandRegistries, item => item?.landRegistryId === row?.landRegistryId) ? colors.white : colors.black  } />
            </>
          </Button>
        )
      }
    ],
    [dispatch, field, getFieldLandRegistries, landRegistries, loading, seeAllOnMap, seeOnMap, selectedLandRegistries, t]
  )

  return (
    <Modal
      id={ id }
      open={ open }
      title={ t('land registries') }
      maxWidth="lg"
      fullWidth
      disableEsc
    >
      <DataTable
        hasPagination={ false }
        hidePagination
        ref={ tableRef }
        data={ loading ? [] : landRegistries }
        columns={ columns }
        loading={ loading }
        id="land-registries-table"
      />
    </Modal>
  )
}

FieldLandRegistryModal.propTypes = {
  id: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  selectedLandRegistries: PropTypes.array.isRequired,
  setSelectedLandRegistries: PropTypes.func.isRequired,
  field: PropTypes.object.isRequired
}

FieldLandRegistryModal.defaultProps = {
}

export default FieldLandRegistryModal
